import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import {
  CatalogOffering, CatalogService, CatalogWarehouse, InventoryLot, InventoryQcInspection, InventoryQcRule, InventorySerial
} from './catalog.service';
import { QcChecklistResultsFormComponent } from './qc-checklist-results-form.component';
import { parseChecklistResults, parseChecklistTemplate, serializeChecklistResults, validateChecklistResults } from './qc-checklist.model';

type QcTab = 'general' | 'results';

@Component({
  standalone: true,
  selector: 'ain-inventory-qc-inspection-detail',
  imports: [
    AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    SelectModule, TextareaModule, QcChecklistResultsFormComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('catalog.qc.inspections.createTitle') : t('catalog.qc.inspections.detailTitle')" [description]="t('catalog.qc.inspections.detailDescription')">
      <ain-inventory-page-hint pageKey="qcDetail" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !inspection()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error() || t('catalog.qc.inspections.notFound')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.qc.inspections.newRecord') : inspection()!.referenceNumber }}</h3>
            <p>{{ isCreateMode() ? t('catalog.qc.inspections.createHint') : inspection()!.status }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/inventory/qc-inspections']">{{ t('catalog.action.backToList') }}</a>
            @if (!isCreateMode() && inspection()!.status !== 'Passed' && inspection()!.status !== 'Failed') {
              <button pButton type="button" [label]="t('catalog.qc.action.completePass')" severity="success" (click)="complete(true)"></button>
              <button pButton type="button" [label]="t('catalog.qc.action.completeFail')" severity="warn" (click)="complete(false)"></button>
            }
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.action.save')" (click)="save()"></button>
            }
          </div>
        </section>

        <section class="tabs">
          <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('catalog.tabs.general') }}</button>
          @if (!isCreateMode()) {
            <button type="button" [class.active]="activeTab() === 'results'" (click)="activeTab.set('results')">{{ t('catalog.qc.resultsTab') }}</button>
          }
        </section>

        @if (activeTab() === 'general') {
          <section class="form-grid">
            <label>
              <span>{{ t('catalog.qc.referenceNumber') }}</span>
              <input pInputText type="text" [(ngModel)]="form.referenceNumber" [disabled]="!isCreateMode()" />
            </label>
            <label>
              <span>{{ t('catalog.qc.inspectionType') }}</span>
              <p-select [options]="inspectionTypeOptions()" [ngModel]="form.inspectionType" (ngModelChange)="onInspectionTypeChange($event)" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.qc.ruleId') }}</span>
              <p-select [options]="ruleOptions()" [ngModel]="form.ruleId" (ngModelChange)="onRuleChange($event)" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.offeringId') }}</span>
              <p-select [options]="offeringOptions()" [ngModel]="form.offeringId" (ngModelChange)="onOfferingChange($event)" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.tracking.lotId') }}</span>
              <p-select [options]="lotOptions()" [ngModel]="form.lotId" (ngModelChange)="onLotChange($event)" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.tracking.serialNumber') }}</span>
              <p-select [options]="serialOptions()" [(ngModel)]="form.serialId" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.warehouseId') }}</span>
              <p-select [options]="warehouseOptions()" [(ngModel)]="form.warehouseId" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
            </label>
          </section>
        }

        @if (activeTab() === 'results' && !isCreateMode()) {
          <section class="results-panel">
            <label class="full">
              <span>{{ t('catalog.qc.resultNotes') }}</span>
              <textarea pTextarea rows="3" [(ngModel)]="form.resultNotes" [disabled]="isReadOnly()"></textarea>
            </label>
            <label>
              <span>{{ t('catalog.qc.failReason') }}</span>
              <input pInputText type="text" [(ngModel)]="form.failReasonCode" [disabled]="isReadOnly()" />
            </label>

            <section class="checklist-section full">
              <header>
                <h4>{{ t('catalog.qc.checklistResults') }}</h4>
                @if (selectedRule()) {
                  <span>{{ selectedRule()!.code }} — {{ selectedRule()!.name }}</span>
                } @else {
                  <span>{{ t('catalog.qc.checklist.defaultTemplate') }}</span>
                }
              </header>
              <ain-qc-checklist-results-form
                [templateJson]="checklistTemplateJson()"
                [ruleId]="form.ruleId || null"
                [ruleCode]="selectedRule()?.code ?? null"
                [readOnly]="isReadOnly()"
                [(resultsJson)]="form.checklistResultsJson" />
            </section>
          </section>
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .tabs { display: flex; gap: 8px; flex-wrap: wrap; margin: 10px 0; }
    .tabs button { border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; padding: 8px 12px; cursor: pointer; }
    .tabs button.active { border-color: #3b82f6; color: #1d4ed8; font-weight: 600; }
    .form-grid, .results-panel { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); padding-top: 10px; }
    .form-grid label, .results-panel label { display: grid; gap: 6px; }
    .full { grid-column: 1 / -1; }
    .form-grid span, .results-panel span { font-size: 12px; color: #475569; font-weight: 600; }
    .checklist-section { display: grid; gap: 10px; width: 100%; min-width: 0; }
    .checklist-section header { display: flex; justify-content: space-between; gap: 8px; align-items: center; flex-wrap: wrap; }
    .checklist-section h4 { margin: 0; font-size: 15px; }
    .checklist-section header span { color: #64748b; font-size: 12px; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class InventoryQcInspectionDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly inspection = signal<InventoryQcInspection | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly lots = signal<InventoryLot[]>([]);
  readonly serials = signal<InventorySerial[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly qcRules = signal<InventoryQcRule[]>([]);
  readonly filterLotId = signal('');
  readonly activeTab = signal<QcTab>('general');

  readonly form = {
    referenceNumber: '',
    inspectionType: 'Inbound',
    ruleId: '',
    offeringId: '',
    lotId: '',
    serialId: '',
    warehouseId: '',
    resultNotes: '',
    failReasonCode: '',
    checklistResultsJson: '{}'
  };

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');
  readonly isReadOnly = computed(() => {
    const status = this.inspection()?.status;
    return status === 'Passed' || status === 'Failed';
  });
  readonly selectedRule = computed(() => {
    const ruleId = this.form.ruleId || this.inspection()?.ruleId;
    if (!ruleId) return null;
    return this.qcRules().find(rule => rule.id === ruleId) ?? null;
  });
  readonly checklistTemplateJson = computed(() => this.selectedRule()?.checklistJson ?? '');
  readonly offeringOptions = computed(() => this.offerings().map(o => ({ label: `${o.code} - ${o.title}`, value: o.id })));
  readonly lotOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.lots().map(l => ({ label: l.lotNumber, value: l.id }))
  ]);
  readonly serialOptions = computed(() => {
    const lotId = this.filterLotId();
    const serials = lotId ? this.serials().filter(s => s.lotId === lotId) : this.serials();
    return [
      { label: this.t('common.none'), value: '' },
      ...serials.map(s => ({ label: s.serialNumber, value: s.id }))
    ];
  });
  readonly warehouseOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.warehouses().map(w => ({ label: `${w.code} - ${w.name}`, value: w.id }))
  ]);
  readonly inspectionTypeOptions = computed(() => [
    { label: this.t('catalog.qc.type.inbound'), value: 'Inbound' },
    { label: this.t('catalog.qc.type.outbound'), value: 'Outbound' }
  ]);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows) });
    this.service.listWarehouses().subscribe({ next: rows => this.warehouses.set(rows) });
    this.service.listQcRules().subscribe({ next: rows => this.qcRules.set(rows) });
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.service.getQcInspection(id).subscribe({
      next: inspection => {
        this.inspection.set(inspection);
        this.form.referenceNumber = inspection.referenceNumber;
        this.form.inspectionType = inspection.inspectionType;
        this.form.ruleId = inspection.ruleId ?? '';
        this.form.offeringId = inspection.offeringId;
        this.form.lotId = inspection.lotId ?? '';
        this.filterLotId.set(inspection.lotId ?? '');
        this.form.serialId = inspection.serialId ?? '';
        this.form.warehouseId = inspection.warehouseId ?? '';
        this.form.resultNotes = inspection.resultNotes;
        this.form.failReasonCode = inspection.failReasonCode;
        this.form.checklistResultsJson = inspection.checklistResultsJson || '{}';
        this.loadTrackingOptions(inspection.offeringId);
        this.loading.set(false);
      },
      error: () => {
        this.inspection.set(null);
        this.error.set(this.t('catalog.qc.inspections.notFound'));
        this.loading.set(false);
      }
    });
  }

  onRuleChange(ruleId: string): void {
    this.form.ruleId = ruleId;
    if (!this.isCreateMode()) {
      this.resetChecklistDraft();
    }
  }

  onInspectionTypeChange(inspectionType: string): void {
    this.form.inspectionType = inspectionType;
    const stillValid = !this.form.ruleId || this.qcRules().some(
      rule => rule.id === this.form.ruleId && rule.isActive && rule.inspectionType === inspectionType
    );
    if (!stillValid) {
      this.form.ruleId = '';
      if (!this.isCreateMode()) {
        this.resetChecklistDraft();
      }
    }
  }

  ruleOptions(): Array<{ label: string; value: string }> {
    return [
      { label: this.t('common.none'), value: '' },
      ...this.qcRules()
        .filter(rule => rule.isActive && rule.inspectionType === this.form.inspectionType)
        .map(rule => ({ label: `${rule.code} - ${rule.name}`, value: rule.id }))
    ];
  }

  onOfferingChange(offeringId: string): void {
    this.form.offeringId = offeringId;
    this.form.lotId = '';
    this.filterLotId.set('');
    this.form.serialId = '';
    this.loadTrackingOptions(offeringId);
  }

  onLotChange(lotId: string): void {
    this.form.lotId = lotId;
    this.filterLotId.set(lotId);
    const serials = lotId ? this.serials().filter(s => s.lotId === lotId) : this.serials();
    if (this.form.serialId && !serials.some(s => s.id === this.form.serialId)) {
      this.form.serialId = '';
    }
  }

  save(): void {
    if (!this.isCreateMode()) return;

    if (!this.form.referenceNumber.trim() || !this.form.offeringId) {
      this.error.set(this.t('catalog.qc.inspections.requiredFields'));
      return;
    }

    this.service.createQcInspection({
      ruleId: this.form.ruleId || null,
      offeringId: this.form.offeringId,
      lotId: this.form.lotId || null,
      serialId: this.form.serialId || null,
      inspectionType: this.form.inspectionType,
      referenceNumber: this.form.referenceNumber.trim(),
      warehouseId: this.form.warehouseId || null
    }).subscribe({
      next: created => void this.router.navigate(['/inventory/qc-inspections', created.id]),
      error: () => this.error.set(this.t('catalog.qc.inspections.createFailed'))
    });
  }

  complete(passed: boolean): void {
    const inspection = this.inspection();
    if (!inspection) return;

    if (!passed && !this.form.failReasonCode.trim()) {
      this.error.set(this.t('catalog.qc.failReasonRequired'));
      return;
    }

    const template = parseChecklistTemplate(this.checklistTemplateJson(), key => this.t(key));
    const results = parseChecklistResults(this.form.checklistResultsJson, template, {
      ruleId: this.form.ruleId || inspection.ruleId,
      ruleCode: this.selectedRule()?.code ?? null
    });
    const invalidItems = validateChecklistResults(results);
    if (invalidItems.length > 0) {
      this.error.set(this.t('catalog.qc.checklist.validationFailed'));
      return;
    }

    this.service.completeQcInspection(inspection.id, {
      passed,
      resultNotes: this.form.resultNotes.trim(),
      failReasonCode: passed ? '' : this.form.failReasonCode.trim(),
      checklistResultsJson: this.form.checklistResultsJson.trim() || '{}'
    }).subscribe({
      next: updated => {
        this.inspection.set(updated);
        this.form.checklistResultsJson = updated.checklistResultsJson || this.form.checklistResultsJson;
        this.error.set('');
      },
      error: () => this.error.set(this.t('catalog.qc.inspections.completeFailed'))
    });
  }

  private resetChecklistDraft(): void {
    const template = parseChecklistTemplate(this.checklistTemplateJson(), key => this.t(key));
    const results = parseChecklistResults('{}', template, {
      ruleId: this.form.ruleId || null,
      ruleCode: this.selectedRule()?.code ?? null
    });
    this.form.checklistResultsJson = serializeChecklistResults(results);
  }

  private loadTrackingOptions(offeringId: string): void {
    if (!offeringId) {
      this.lots.set([]);
      this.serials.set([]);
      return;
    }

    this.service.listInventoryLots(offeringId).subscribe({ next: rows => this.lots.set(rows) });
    this.service.listInventorySerials(offeringId).subscribe({ next: rows => this.serials.set(rows) });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
